import { UI, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Box, Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import {
  TableContent,
  TableContentItemCaptions,
  TableContentItemColumn,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFundsAssetContext } from '../../../../../contexts/products/Funds';
import { useFundsAssetController } from '../../../../../hooks/products/Funds';
import { LateralBar } from '../../../components/LateralBar';
import { fundsConstants } from '../constants';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';

import { FundsListItemTypes } from '.';

export const FundsListItemDesktop: React.FC<FundsListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdNormalized;
  const {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit,
    control,
    hasError,
    helpText,
    currentValueFormatted,
    currentValue,
    productsDetailText,
  } = useFundsAssetController(offer);
  const { setModalOpen, setOfferSelected } = useFundsAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue };

  const { Button, isSelected, forceLoading } = infoGetter(
    'funds',
    completeOffer,
    'productIdNormalized'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const getFlex = (index: number) => {
    const currentHead = fundsConstants.tableHead.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      h="80px"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <TableContentItemColumn flex={getFlex(0)}>
        <UI.Tooltip testID="details-tooltip" label={productsDetailText}>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon
              name={offer.closedFundNormalized ? 'Warning' : 'Info'}
              size="xs"
              {...(offer.closedFundNormalized && { color: Color['feedback.attention.default'] })}
            />
          </Pressable>
        </UI.Tooltip>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(1)}>
        <VStack pr={Spacing['inline.xxxs']} space={Spacing['stack.quarck']}>
          <TableContentItemText>{offer.productNameNormalized}</TableContentItemText>
          <TableContentItemCaptions icons={offer.captions} />
        </VStack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(2)}>
        <TableContentItemText>{offer.productCategoryNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(3)}>
        <TableContentItemText>{offer.profitabilityTwelveMonthsNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(4)}>
        <Stack pr={Spacing['inline.xxxs']}>
          <TableContentItemText>{offer.administrationFeeNormalized}</TableContentItemText>
        </Stack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(5)}>
        <TableContentItemText>
          {offer.quotationRedemptionTermFormattedNormalized}
        </TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(6)}>
        <RiskRating
          currentRiskRating={Number(offer.riskRatingLevelNormalized) || 0}
          tooltip={offer.riskRatingNormalized || ''}
        />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(7)}>
        <Stack pr={Spacing['inline.xxxs']}>
          <InputMoney
            id={`fixedIncome-input${id}`}
            name="createAppliedAmount"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            currentValue={currentValueFormatted}
            hasFocus={isFocused}
            minValue={offer.applicationMinimumAmount || 0}
            isDisabled={isSelected}
            customInputProps={{
              ...{ ...(isFocused && hasError && { error: hasError }) },
              ...{ ...(isFocused && !hasError && { helpText }) },
            }}
          />
        </Stack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(8)}>
        <Box w={'100%'}>
          <Button
            id={selectedTab.typeGetterId}
            isDisabled={typeof hasError?.message === 'string'}
            offer={completeOffer}
            productContextConstant={selectedTab}
          />
        </Box>
      </TableContentItemColumn>
    </TableContent>
  );
};
